import { lastActivityMs, type ChatLike } from './chatListRules';

/**
 * Chat lette dal telefono: WAHA riporta 0 non letti, ma il conteggio del CRM
 * (DB + incrementi realtime) restava quello vecchio finché non si apriva la
 * chat dal CRM. Se WAHA dice 0, la chat è letta fino al suo ultimo messaggio.
 *
 * Modulo puro (nessun accesso a store, DB o WAHA).
 */

/** Istante (ms) fino a cui WAHA considera letta la chat; undefined se WAHA non dice 0. */
export function wahaReadUpToMs(chat: ChatLike & { unreadCount?: number }): number | undefined {
  if (chat.unreadCount !== 0) return undefined;
  return lastActivityMs(chat);
}

/**
 * Conteggio da mostrare: 0 se WAHA ha dichiarato la chat letta e da allora non è
 * arrivato nulla di più recente (un messaggio realtime successivo resta non letto).
 */
export function reconcileUnreadWithWaha(
  count: number,
  chat: ChatLike & { wahaReadUpToMs?: number },
): number {
  if (chat.wahaReadUpToMs === undefined) return count;
  // WAHA ha i secondi, il realtime può avere i millisecondi dello stesso messaggio:
  // si tollera meno di un secondo di differenza.
  return chat.wahaReadUpToMs + 999 >= lastActivityMs(chat) ? 0 : count;
}

/** Ritardo massimo con cui un messaggio vero puo' arrivare fuori ordine dal realtime. */
const LATE_MESSAGE_TOLERANCE_MS = 2 * 60 * 1000;

/**
 * Un messaggio in arrivo dal realtime va contato come non letto? (segnalazioni
 * 5b5a4b8c e d42d053b: "99+" su chat gia' lette). No se:
 * - WAHA ha gia' dichiarato la chat letta fino a quel momento (letta dal telefono);
 * - e' piu' vecchio dell'ultima attivita' nota della chat oltre la tolleranza:
 *   e' un messaggio recuperato in ritardo (sincronizzazione dopo una riconnessione),
 *   non uno nuovo, e se dopo c'e' gia' stata attivita' (anche una nostra risposta)
 *   non e' lavoro da leggere. Il DB fa lo stesso: dopo una risposta non conta.
 */
export function countsAsNewUnread(
  chat: (ChatLike & { wahaReadUpToMs?: number }) | undefined,
  messageMs: number,
): boolean {
  if (!chat) return true;
  if (chat.wahaReadUpToMs !== undefined && messageMs <= chat.wahaReadUpToMs + 999) return false;
  const last = lastActivityMs(chat);
  return !last || messageMs >= last - LATE_MESSAGE_TOLERANCE_MS;
}
